/**
 * Room-code vocabulary. `ROOM_CODE_ALPHABET` + `ROOM_CODE_LENGTH` are
 * consumed by `@bn-games/room-server`'s `RoomManager.generateCode` and by
 * every game app's join/create screens (client-side validation and
 * display); `sanitizeRoomCodeInput` is the join-field sanitizer all four
 * apps share. `normalizeRoomCode` / `isCompleteRoomCode` below have no
 * consumer yet — they exist for a future server-side normalization path
 * (see Open question #4 in the M1 plan) rather than one that exists today.
 * Lifted out of `RoomManager.ts` and four near-identical per-app copies so
 * the alphabet and length live in one place — see the M1 "Token contract &
 * hygiene" plan, "Where the room-code alphabet lives".
 *
 * The alphabet deliberately excludes `I`/`O`/`0`/`1` — characters easily
 * confused with each other or with `1`/`L` when read aloud or handwritten.
 */
export const ROOM_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

/**
 * 32 symbols. `RoomManager.generateCode` maps a random byte to a character
 * via `byte % ROOM_CODE_ALPHABET.length`; 256 % 32 === 0, so every symbol is
 * equally likely and no rejection sampling is needed. Do not resize this
 * alphabet to a non-power-of-two without adding rejection sampling to that
 * mapping.
 */
export const ROOM_CODE_LENGTH = 6;

/** Upper-cases a room code. The server does not trim or strip characters. */
export function normalizeRoomCode(input: string): string {
  return input.toUpperCase();
}

/** True iff `input` is exactly `ROOM_CODE_LENGTH` characters, all in `ROOM_CODE_ALPHABET`. */
export function isCompleteRoomCode(input: string): boolean {
  if (input.length !== ROOM_CODE_LENGTH) return false;
  for (const char of input) {
    if (!ROOM_CODE_ALPHABET.includes(char)) return false;
  }
  return true;
}

/**
 * Upper-cases free-form join-field input, drops anything outside
 * `ROOM_CODE_ALPHABET`, and truncates to `ROOM_CODE_LENGTH` — the client-side
 * counterpart to `normalizeRoomCode` (which only upper-cases, matching the
 * server's own leniency). Forgives lower case and stray punctuation typed or
 * pasted into a join field; filtering is character-wise, not a parser, so a
 * code embedded in a sentence keeps that sentence's letters too.
 */
export function sanitizeRoomCodeInput(raw: string): string {
  const kept = raw
    .toUpperCase()
    .split("")
    .filter((ch) => ROOM_CODE_ALPHABET.includes(ch));
  return kept.slice(0, ROOM_CODE_LENGTH).join("");
}
